import { useAuth } from '../hooks/useAuth.jsx';
import { GoogleButton, OrDivider } from './GoogleButton.jsx';

/** Where a Facebook or Instagram sign-in starts: the server sends the browser on to the provider. */
export function signInStartUrl(provider, { role, next, link } = {}) {
  const query = new URLSearchParams();
  if (role) query.set('role', role);
  if (next) query.set('next', next);
  if (link) query.set('link', '1');
  const qs = query.toString();
  return `/api/auth/oauth/${provider}/start${qs ? `?${qs}` : ''}`;
}

function FacebookMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-5 shrink-0">
      <circle cx="12" cy="12" r="12" fill="#1877F2" />
      <path
        fill="#fff"
        d="M13.4 19.5v-6.1h2.05l.31-2.38H13.4V9.5c0-.69.19-1.16 1.18-1.16h1.26V6.21a16.9 16.9 0 0 0-1.84-.09c-1.82 0-3.07 1.11-3.07 3.15v1.75H8.88v2.38h2.05v6.1h2.47z"
      />
    </svg>
  );
}

function InstagramMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="size-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.3" cy="6.7" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** A provider's button, the same size as Google's so the three line up. */
export function ProviderButton({ provider, href, note }) {
  const label = provider === 'facebook' ? 'Facebook' : 'Instagram';
  return (
    <a
      href={href}
      className="mx-auto flex w-full max-w-[320px] items-center gap-3 rounded border border-rule-strong bg-paper-raised px-3 py-2 text-sm font-medium text-ink hover:bg-paper-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
    >
      {provider === 'facebook' ? <FacebookMark /> : <InstagramMark />}
      <span className="flex-1 text-center">
        Continue with {label}
        {note && <span className="block text-2xs font-normal text-ink-muted">{note}</span>}
      </span>
    </a>
  );
}

/**
 * Every way to sign in that this Affily has set up: Google on the page,
 * Facebook and Instagram by way of their own sites. Shows nothing at all when
 * none is set up, so email and password stand alone.
 */
export function SignInButtons({ role, next, onSuccess, onError }) {
  const { googleEnabled, facebookEnabled, instagramEnabled } = useAuth();
  if (!googleEnabled && !facebookEnabled && !instagramEnabled) return null;

  return (
    <div className="space-y-3">
      <GoogleButton role={role} onSuccess={onSuccess} onError={onError} divider={false} />
      {facebookEnabled && <ProviderButton provider="facebook" href={signInStartUrl('facebook', { role, next })} />}
      {instagramEnabled && (
        <ProviderButton
          provider="instagram"
          href={signInStartUrl('instagram', { role, next })}
          note="Business or creator accounts"
        />
      )}
      <OrDivider />
    </div>
  );
}
